<template>
  <div class="conflicts-page">
    <div class="page-wrapper">
      <header class="page-header">
        <div class="header-text">
          <h1><i class="mdi mdi-face-recognition"></i> Successful Face Scans</h1>
          <p>Accepted matches that cleared the identity threshold and were sent to EdClock.</p>
        </div>
        <div class="header-actions">
          <router-link to="/conflicts" class="btn ghost">
            <i class="mdi mdi-shield-alert"></i> Conflicts
          </router-link>
          <router-link to="/conflicts/students" class="btn ghost">
            <i class="mdi mdi-account-group"></i> By student
          </router-link>
          <button class="btn ghost" :disabled="loading" @click="refresh">
            <i class="mdi" :class="loading ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></i>
            Refresh
          </button>
        </div>
      </header>

      <div v-if="toast" class="toast error">
        <i class="mdi mdi-alert-circle"></i>
        <span>{{ toast }}</span>
        <button class="toast-close" @click="toast = null"><i class="mdi mdi-close"></i></button>
      </div>

      <section class="stat-grid">
        <div class="stat-card today">
          <span class="stat-label">Today</span>
          <span class="stat-value">{{ stats.today || 0 }}</span>
        </div>
        <div class="stat-card in">
          <span class="stat-label">Clock in</span>
          <span class="stat-value">{{ stats.byMode.IN || 0 }}</span>
        </div>
        <div class="stat-card out">
          <span class="stat-label">Clock out</span>
          <span class="stat-value">{{ stats.byMode.OUT || 0 }}</span>
        </div>
        <div class="stat-card total">
          <span class="stat-label">All time</span>
          <span class="stat-value">{{ stats.total || 0 }}</span>
        </div>
      </section>

      <section class="filters">
        <div class="status-tabs">
          <button
            v-for="tab in modeTabs"
            :key="tab.value"
            class="tab"
            :class="{ active: mode === tab.value }"
            @click="setMode(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="search-box">
          <i class="mdi mdi-magnify"></i>
          <input
            v-model="q"
            type="text"
            placeholder="Search name or ID…"
            @input="debouncedReload"
          />
        </div>
      </section>

      <section class="table-card">
        <div v-if="loading" class="empty-state">
          <i class="mdi mdi-loading mdi-spin"></i>
          <p>Loading scans…</p>
        </div>

        <div v-else-if="scans.length === 0" class="empty-state">
          <i class="mdi mdi-check-circle-outline"></i>
          <p>No successful scans yet.</p>
        </div>

        <table v-else class="conflict-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>EdClock ID</th>
              <th>Mode</th>
              <th>Similarity</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="scan in scans"
              :key="scan._id"
              :class="{ selected: selected && selected._id === scan._id }"
              @click="selected = scan"
            >
              <td class="col-student">
                <div class="student-name">{{ scan.studentName || '—' }}</div>
                <div class="student-id">{{ scan.biometricsStudentId || '' }}</div>
              </td>
              <td class="mono">{{ scan.edClockStudentId || '—' }}</td>
              <td>
                <span v-if="scan.mode" class="mode-badge" :class="String(scan.mode).toLowerCase()">
                  {{ scan.mode }}
                </span>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <span class="score">{{ formatScore(scan.similarity) }}</span>
                <span v-if="scan.gap != null" class="gap">gap {{ scan.gap.toFixed(1) }}%</span>
              </td>
              <td class="col-when">{{ formatTime(scan.occurredAt) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="pagination.pages > 1" class="pagination">
          <button class="btn ghost" :disabled="pagination.page <= 1" @click="goToPage(pagination.page - 1)">
            Previous
          </button>
          <span>Page {{ pagination.page }} of {{ pagination.pages }} · {{ pagination.total }} entries</span>
          <button
            class="btn ghost"
            :disabled="pagination.page >= pagination.pages"
            @click="goToPage(pagination.page + 1)"
          >
            Next
          </button>
        </div>
      </section>
    </div>

    <div v-if="selected" class="drawer-backdrop" @click.self="selected = null">
      <aside class="drawer">
        <header class="drawer-header">
          <div>
            <h2>{{ selected.studentName || 'Successful scan' }}</h2>
            <p class="drawer-time">{{ formatTime(selected.occurredAt) }}</p>
          </div>
          <button class="icon-btn" @click="selected = null"><i class="mdi mdi-close"></i></button>
        </header>
        <div class="drawer-body">
          <dl class="detail-grid">
            <div>
              <dt>Biometrics ID</dt>
              <dd>{{ selected.biometricsStudentId || '—' }}</dd>
            </div>
            <div>
              <dt>EdClock ID</dt>
              <dd>{{ selected.edClockStudentId || '—' }}</dd>
            </div>
            <div>
              <dt>Mode</dt>
              <dd>{{ selected.mode || '—' }}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>{{ selected.source || '—' }}</dd>
            </div>
            <div>
              <dt>Similarity</dt>
              <dd>{{ formatScore(selected.similarity) }}</dd>
            </div>
            <div>
              <dt>Threshold</dt>
              <dd>{{ selected.threshold ? `${selected.threshold}%` : '—' }}</dd>
            </div>
          </dl>

          <div v-if="selected.candidates && selected.candidates.length">
            <h3>Match candidates</h3>
            <div v-for="candidate in selected.candidates" :key="candidate.studentId" class="candidate">
              <div class="candidate-head">
                <span>{{ candidate.fullName || candidate.studentId }}</span>
                <span class="score">{{ formatScore(candidate.similarity) }}</span>
              </div>
              <div class="candidate-meta">
                EdClock {{ candidate.edClockStudentId || '—' }} · {{ candidate.studentId }}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'SuccessScansView',
  data() {
    return {
      scans: [],
      stats: { total: 0, today: 0, byMode: {} },
      pagination: { page: 1, pages: 1, total: 0, limit: 25 },
      mode: 'all',
      q: '',
      modeTabs: [
        { value: 'all', label: 'All' },
        { value: 'IN', label: 'Clock in' },
        { value: 'OUT', label: 'Clock out' },
      ],
      loading: false,
      selected: null,
      toast: null,
      reloadTimer: null,
    };
  },
  created() {
    this.refresh();
  },
  beforeUnmount() {
    clearTimeout(this.reloadTimer);
  },
  methods: {
    apiUrl(path) {
      const base = String(process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
      return `${base}${path}`;
    },
    formatTime(value) {
      return value ? moment(value).format('MMM D, YYYY · h:mm A') : '—';
    },
    formatScore(value) {
      return typeof value === 'number' ? `${value.toFixed(2)}%` : '—';
    },
    setMode(value) {
      this.mode = value;
      this.pagination.page = 1;
      this.refresh();
    },
    debouncedReload() {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.refresh();
      }, 350);
    },
    goToPage(page) {
      this.pagination.page = page;
      this.refresh();
    },
    async refresh() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          mode: this.mode,
          page: String(this.pagination.page),
          limit: String(this.pagination.limit),
        });
        if (this.q) params.set('q', this.q);

        const [listResponse, statsResponse] = await Promise.all([
          fetch(this.apiUrl(`/api/scans?${params.toString()}`)),
          fetch(this.apiUrl('/api/scans/stats')),
        ]);

        if (!listResponse.ok) throw new Error('Failed to load scans');

        const list = await listResponse.json();
        this.scans = list.items || [];
        this.pagination = { ...this.pagination, ...list.pagination };

        if (statsResponse.ok) {
          const stats = await statsResponse.json();
          this.stats = {
            total: stats.total || 0,
            today: stats.today || 0,
            byMode: stats.byMode || {},
          };
        }
      } catch (error) {
        this.toast = error.message || 'Failed to load scans';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.conflicts-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  padding: 2rem 1rem 4rem;
}

.page-wrapper {
  max-width: 1240px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.header-text h1 {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.35rem;
}

.header-text h1 i {
  color: #22c55e;
}

.header-text p {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.header-actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1.1rem;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.ghost {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.icon-btn {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1.4rem;
  cursor: pointer;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

.toast.error {
  background: #fee2e2;
  color: #991b1b;
}

.toast-close {
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
  border-left: 4px solid #cbd5e1;
}

.stat-card.today,
.stat-card.total {
  border-left-color: #22c55e;
}

.stat-card.in {
  border-left-color: #42b983;
}

.stat-card.out {
  border-left-color: #f59e0b;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.status-tabs {
  display: flex;
  background: #fff;
  border-radius: 10px;
  padding: 0.25rem;
}

.tab {
  border: none;
  background: transparent;
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
}

.tab.active {
  background: #22c55e;
  color: #fff;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 260px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
}

.search-box input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.875rem;
  background: transparent;
}

.table-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
}

.conflict-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.conflict-table th {
  text-align: left;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.conflict-table td {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.conflict-table tbody tr {
  cursor: pointer;
}

.conflict-table tbody tr:hover,
.conflict-table tbody tr.selected {
  background: #f8fafc;
}

.student-name {
  font-weight: 600;
  color: #1e293b;
}

.student-id,
.mono,
.candidate-meta {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  color: #94a3b8;
}

.mono {
  font-size: 0.85rem;
  color: #1e293b;
}

.mode-badge {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.mode-badge.in {
  background: #dcfce7;
  color: #166534;
}

.mode-badge.out {
  background: #ffedd5;
  color: #9a3412;
}

.score {
  font-weight: 700;
  color: #15803d;
}

.gap,
.muted,
.col-when {
  color: #94a3b8;
  font-size: 0.75rem;
}

.gap {
  display: block;
  margin-top: 0.15rem;
}

.empty-state {
  padding: 4rem 1rem;
  text-align: center;
  color: #94a3b8;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  font-size: 0.85rem;
  color: #64748b;
}

.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: flex-end;
  z-index: 9999;
}

.drawer {
  width: min(480px, 100%);
  background: #fff;
  height: 100%;
  overflow-y: auto;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.drawer-header h2 {
  font-size: 1.3rem;
  margin: 0 0 0.25rem;
  color: #1e293b;
}

.drawer-time {
  margin: 0;
  font-size: 0.82rem;
  color: #94a3b8;
}

.drawer-body {
  padding: 1.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 0 0 1.5rem;
}

.detail-grid dt {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: 0.2rem;
}

.detail-grid dd {
  margin: 0;
  font-size: 0.88rem;
  color: #1e293b;
  word-break: break-all;
}

.drawer-body h3 {
  font-size: 0.95rem;
  margin: 0 0 0.75rem;
}

.candidate {
  margin-bottom: 0.85rem;
}

.candidate-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e293b;
}

.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
